import { format, parseISO } from 'date-fns';

import type {
  SponsorTypeCount,
  SubTypeCount,
  Subscription,
} from '@/types/service/internalAnalytics';

// --- Chart Color Palettes ---

export const CHART_COLORS = {
  // For subscription chart
  active: '#10b981', // emerald-500
  expired: '#6b7280', // gray-500

  // For pie charts (12 colors for varied data)
  pie: [
    '#3b82f6', // blue-500
    '#10b981', // emerald-500
    '#f59e0b', // amber-500
    '#ef4444', // red-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#06b6d4', // cyan-500
    '#84cc16', // lime-500
    '#f97316', // orange-500
    '#6366f1', // indigo-500
    '#14b8a6', // teal-500
    '#a855f7', // purple-500
  ],

  // For line chart
  line: '#3b82f6', // blue-500
};

// --- Subscription Data Transformations ---

export interface GroupedSubscriptionData {
  date: string; // Formatted date for display
  dateKey: string; // YYYY-MM-DD for sorting
  active: number; // Count of active trials ending on this date
  expired: number; // Count of expired/disabled trials
  subscriptions: Subscription[]; // Original records for drill-down
}

/**
 * Group subscriptions by their end_at date for bar chart visualization
 */
export function groupSubscriptionsByEndDate(
  subscriptions: Subscription[],
): GroupedSubscriptionData[] {
  const grouped = new Map<string, GroupedSubscriptionData>();

  for (const sub of subscriptions) {
    // Skip subscriptions without end_at date
    if (!sub.end_at) continue;

    try {
      const dateKey = format(parseISO(sub.end_at), 'yyyy-MM-dd');
      const displayDate = format(parseISO(sub.end_at), 'MMM dd');

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, {
          date: displayDate,
          dateKey,
          active: 0,
          expired: 0,
          subscriptions: [],
        });
      }

      const group = grouped.get(dateKey)!;
      group.subscriptions.push(sub);

      if (sub.disabled) {
        group.expired++;
      } else {
        group.active++;
      }
    } catch {
      // Skip subscriptions with invalid dates
      continue;
    }
  }

  // Sort by date ascending
  return Array.from(grouped.values()).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

// --- Currency Formatting ---

/**
 * Format large currency amounts (total_amount is in dollars)
 */
export function formatCurrency(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(2)}B`;
  }
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(2)}M`;
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(2)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

/**
 * Format numbers with comma separators (full number, no abbreviations)
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

// --- Pie Chart Data Transformation ---

export interface PieChartData {
  name: string;
  value: number;
  color: string;
}

export function transformSponsorTypeData(data: SponsorTypeCount[]): PieChartData[] {
  return data.map((item, index) => ({
    name: item.sponsor_type,
    value: item.count,
    color: CHART_COLORS.pie[index % CHART_COLORS.pie.length],
  }));
}

export function transformSubTypeData(data: SubTypeCount[]): PieChartData[] {
  return data.map((item, index) => ({
    name: item.sub_type,
    value: item.count,
    color: CHART_COLORS.pie[index % CHART_COLORS.pie.length],
  }));
}
